import React, { lazy, Suspense, useState, useEffect } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion'
import Navbar from './components/layout/Navbar.jsx'
import Footer from './components/layout/Footer.jsx'
import ScrollToTop from './components/layout/ScrollToTop.jsx'

// Lazy load all components to shrink the entry bundle
const Home = lazy(() => import('./pages/Home.jsx'))
const Services = lazy(() => import('./pages/Services.jsx'))
const ServiceDetail = lazy(() => import('./pages/ServiceDetail.jsx'))
const Portfolio = lazy(() => import('./pages/Portfolio.jsx'))
const CaseStudy = lazy(() => import('./pages/CaseStudy.jsx'))
const About = lazy(() => import('./pages/About.jsx'))
const Blog = lazy(() => import('./pages/Blog.jsx'))
const BlogPost = lazy(() => import('./pages/BlogPost.jsx'))
const Contact = lazy(() => import('./pages/Contact.jsx'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.jsx'))
const TermsOfService = lazy(() => import('./pages/TermsOfService.jsx'))
const NotFound = lazy(() => import('./pages/NotFound.jsx'))

const AnimatedCursor = lazy(() => import('./components/ui/AnimatedCursor.jsx'))
const Chatbot = lazy(() => import('./components/ui/Chatbot.jsx'))

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 16, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit: { opacity: 0, y: -8, filter: 'blur(2px)', transition: { duration: 0.25, ease: 'easeIn' } },
}

function PageTransition({ children }) {
  return (
    <m.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {children}
    </m.div>
  )
}

function App() {
  const location = useLocation()
  const [showGlobalUI, setShowGlobalUI] = useState(false)

  useEffect(() => {
    // Delay non-critical UI to free up the main thread for LCP
    const timer = setTimeout(() => setShowGlobalUI(true), 2500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <LazyMotion features={domAnimation} strict>
    <div className="min-h-screen bg-black text-white flex flex-col font-sans selection:bg-[#CCFF00]/20 selection:text-white">

      <ScrollToTop />
      {showGlobalUI && <Suspense fallback={null}><AnimatedCursor /></Suspense>}
      <Navbar />
      <div className="flex-1">
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            {/* All routes are now lazy-loaded with individual Suspense boundaries for maximum code-splitting */}
            <Route path="/" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><Home /></Suspense>} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/services" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><Services /></Suspense></PageTransition>} />
            <Route path="/services/:slug" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><ServiceDetail /></Suspense></PageTransition>} />
            <Route path="/portfolio" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><Portfolio /></Suspense></PageTransition>} />
            <Route path="/portfolio/:slug" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><CaseStudy /></Suspense></PageTransition>} />
            <Route path="/about" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><About /></Suspense></PageTransition>} />
            <Route path="/blog" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><Blog /></Suspense></PageTransition>} />
            <Route path="/blog/:slug" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><BlogPost /></Suspense></PageTransition>} />
            <Route path="/contact" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><Contact /></Suspense></PageTransition>} />
            <Route path="/privacy-policy" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><PrivacyPolicy /></Suspense></PageTransition>} />
            <Route path="/terms-of-service" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><TermsOfService /></Suspense></PageTransition>} />
            <Route path="*" element={<PageTransition><Suspense fallback={<div className="min-h-screen bg-black" />}><NotFound /></Suspense></PageTransition>} />
          </Routes>
        </AnimatePresence>
      </div>
      {showGlobalUI && <Suspense fallback={null}><Chatbot /></Suspense>}
      <Footer />
    </div>
    </LazyMotion>
  )
}

export default App
